import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { FileText, Search, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const Auditoria = () => {
  const { user } = useAuth();
  const [auditLogs, setAuditLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [logEntries, setLogEntries] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (user?.is_superuser) {
      fetchAuditLogs();
    }
  }, [user]);

  const fetchAuditLogs = async () => {
    try {
      const response = await axios.get('/audit-logs');
      setAuditLogs(response.data.logs);
    } catch (error) {
      toast.error('Error al cargar los logs de auditoría');
    }
  };

  const loadLogFile = async (filename) => {
    setLoadingLogs(true);
    try {
      const response = await axios.get(`/audit-logs/${filename}`);
      setLogEntries(response.data.entries);
      setSelectedLog(filename);
    } catch (error) {
      toast.error('Error al cargar el archivo de log');
    } finally {
      setLoadingLogs(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const filteredLogEntries = logEntries.filter((entry) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      entry.user?.toLowerCase().includes(searchLower) ||
      entry.action?.toLowerCase().includes(searchLower) ||
      entry.entity_id?.toLowerCase().includes(searchLower) ||
      entry.entity_display?.toLowerCase().includes(searchLower) ||
      JSON.stringify(entry.changes || {}).toLowerCase().includes(searchLower)
    );
  });

  if (!user?.is_superuser) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-gray-600">
              Solo los superusuarios pueden acceder a los logs de auditoría.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="auditoria-container">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Auditoría</h1>
        <p className="text-gray-600 mt-1">
          Registro de cambios y operaciones del sistema (últimos 7 días)
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Log Files List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText size={20} />
              Archivos de Log
            </CardTitle>
            <p className="text-sm text-gray-600">Se mantienen los últimos 7 días</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {auditLogs.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">
                  No hay logs disponibles
                </p>
              ) : (
                auditLogs.map((log) => (
                  <button
                    key={log.filename}
                    onClick={() => loadLogFile(log.filename)}
                    className={`w-full text-left p-4 rounded-lg border transition-all ${
                      selectedLog === log.filename
                        ? 'bg-blue-50 border-blue-500 shadow-md'
                        : 'hover:bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-gray-500" />
                        <div>
                          <p className="font-medium text-sm">{log.date}</p>
                          <p className="text-xs text-gray-500">{formatBytes(log.size)}</p>
                        </div>
                      </div>
                      {selectedLog === log.filename && (
                        <Badge className="bg-blue-600">Activo</Badge>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Log Entries Display */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Search size={20} />
                Entradas del Log
              </CardTitle>
              {selectedLog && (
                <Badge variant="outline" className="text-base">
                  {logEntries.length} entradas
                </Badge>
              )}
            </div>
            {selectedLog && (
              <div className="mt-4">
                <Input
                  type="text"
                  placeholder="Buscar por usuario, acción, Nº GOYA o cambios..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="max-w-md"
                />
              </div>
            )}
          </CardHeader>
          <CardContent>
            {loadingLogs ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Cargando log...</p>
                </div>
              </div>
            ) : !selectedLog ? (
              <div className="text-center py-12">
                <FileText className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                <p className="text-gray-500 font-medium">
                  Selecciona un archivo de log para ver sus entradas
                </p>
              </div>
            ) : filteredLogEntries.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">
                  {searchTerm
                    ? 'No se encontraron entradas que coincidan con la búsqueda'
                    : 'No hay entradas en este log'}
                </p>
              </div>
            ) : (
              <div className="space-y-4 max-h-[700px] overflow-y-auto">
                {filteredLogEntries.map((entry, index) => (
                  <div
                    key={index}
                    className="p-4 border-2 rounded-lg hover:shadow-md transition-shadow bg-white"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Badge
                          className={
                            entry.action === 'CREATE'
                              ? 'bg-green-500 hover:bg-green-600'
                              : entry.action === 'UPDATE'
                              ? 'bg-blue-500 hover:bg-blue-600'
                              : 'bg-red-500 hover:bg-red-600'
                          }
                        >
                          {entry.action}
                        </Badge>
                        <span className="text-sm font-semibold text-gray-800">
                          {entry.user}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 font-medium">
                        {formatTimestamp(entry.timestamp)}
                      </span>
                    </div>

                    <div className="text-sm space-y-2">
                      {entry.entity_display && (
                        <p className="text-gray-700">
                          <span className="font-bold text-blue-600">Nº GOYA:</span>{' '}
                          <span className="font-mono bg-blue-50 px-2 py-1 rounded">
                            {entry.entity_display}
                          </span>
                        </p>
                      )}
                      <p className="text-gray-600 text-xs">
                        <span className="font-semibold">ID:</span>{' '}
                        <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                          {entry.entity_id}
                        </code>
                      </p>

                      {entry.changes && Object.keys(entry.changes).length > 0 && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <p className="font-bold text-xs uppercase text-gray-700 mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                            Cambios Realizados:
                          </p>
                          <div className="space-y-3">
                            {Object.entries(entry.changes).map(([field, change]) => (
                              <div key={field} className="text-xs border-l-2 border-gray-300 pl-3">
                                <span className="font-bold text-gray-800 uppercase">
                                  {field}:
                                </span>
                                {change.old !== undefined && change.new !== undefined ? (
                                  <div className="ml-2 mt-2 space-y-1">
                                    <div className="flex items-start gap-2 text-red-700 bg-red-50 p-2 rounded">
                                      <span className="font-bold">−</span>
                                      <div className="flex-1">
                                        <span className="font-semibold">Anterior:</span>{' '}
                                        {typeof change.old === 'object'
                                          ? JSON.stringify(change.old)
                                          : String(change.old) || '(vacío)'}
                                      </div>
                                    </div>
                                    <div className="flex items-start gap-2 text-green-700 bg-green-50 p-2 rounded">
                                      <span className="font-bold">+</span>
                                      <div className="flex-1">
                                        <span className="font-semibold">Nuevo:</span>{' '}
                                        {typeof change.new === 'object'
                                          ? JSON.stringify(change.new)
                                          : String(change.new) || '(vacío)'}
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <pre className="ml-2 mt-1 text-xs bg-white p-2 rounded border overflow-x-auto">
                                    {JSON.stringify(change, null, 2)}
                                  </pre>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auditoria;
